import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "../lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "success";
type Size = "sm" | "md" | "lg" | "xl";

const variants: Record<Variant, string> = {
  primary:
    "bg-brand-600 text-white active:bg-brand-800 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-brand-700",
  secondary:
    "bg-white text-ink border border-slate-300 active:bg-slate-100 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-slate-50",
  ghost:
    "bg-transparent text-ink-soft active:bg-slate-200 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-slate-100",
  danger:
    "bg-stop text-white active:brightness-90 [@media(hover:hover)_and_(pointer:fine)]:hover:brightness-95",
  success:
    "bg-go text-white active:brightness-90 [@media(hover:hover)_and_(pointer:fine)]:hover:brightness-95"
};

const sizes: Record<Size, string> = {
  sm: "h-9 px-3 text-sm rounded-lg",
  md: "h-11 px-4 text-sm rounded-lg",
  lg: "h-12 px-5 text-base rounded-xl",
  xl: "h-16 px-6 text-lg rounded-2xl"
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  block?: boolean;
  children?: ReactNode;
}

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  block = false,
  className,
  disabled,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      className={cn(
        "inline-flex cursor-pointer touch-manipulation select-none items-center justify-center gap-2 font-semibold transition [-webkit-tap-highlight-color:transparent] disabled:cursor-not-allowed disabled:opacity-50",
        variants[variant],
        sizes[size],
        block && "w-full",
        className
      )}
      disabled={disabled || loading}
    >
      {loading && <Spinner className="h-4 w-4" />}
      {children}
    </button>
  );
}

export function Spinner({ className = "h-8 w-8" }: { className?: string }) {
  return (
    <svg
      className={cn("shrink-0 animate-spin", className)}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-90"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}
